import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import { makeAutoObservable, runInAction } from 'mobx'
import dayjs from 'dayjs'
import { store } from './store'
import { Auction, BidsLive, Solvency } from '../models/Auction'
import { history } from '../..'
import { AuthorizedModInterface } from '../models/User'
import agent from '../api/agent'

export default class BidsStore {
  auctionDetails: Auction | undefined = undefined

  bids: BidsLive[] = []

  hubConnection: HubConnection | null = null

  latestBidder: BidsLive | null = null

  bidQueue: Promise<void> | null = null

  currentBid = 0

  deadline: any = undefined

  loadingButton = false

  loadingInitial = true

  unauthorizedUser = false

  loadingAttachBtn = false

  userCount = 0

  queuePosition = 0

  queueTimer = 0

  convertedBid = 0

  constructor() {
    makeAutoObservable(this)
  }

  setLoadingInitial = (state: boolean): void => {
    this.loadingInitial = state
  }

  setLoadingButton = (state: boolean): void => {
    this.loadingButton = state
  }

  setCurrentBid = (value: number): void => {
    this.currentBid = value
  }

  setConvertedBid = (value: number): void => {
    this.convertedBid = value
  }

  setUnauthorizedUser = (state: boolean): void => {
    this.unauthorizedUser = state
  }

  loadAuctionDetails = async (auctionId: string): Promise<void> => {
    const { status, data: auctionData } = await agent.Auctions.details(auctionId)

    if (status === 200) {
      runInAction(() => {
        if (!store.userStore.isAdmin && auctionData.public === 1) {
          this.privateAuctionValidation(auctionData, auctionId)
        }
        this.auctionFilter(auctionData)
      })
    }
  }

  accessTokenFactory = () => store.userStore.user?.token || ''

  createHubConnection = (auctionId: string) => {
    if (auctionId) {
      this.setLoadingInitial(true)
      this.hubConnection = new HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_BID_URL}?auctionId=${auctionId}`, {
          accessTokenFactory: this.accessTokenFactory,
        })
        .withAutomaticReconnect()
        .configureLogging(LogLevel.None)
        .build()

      this.hubConnection
        .start()
        .catch((error) => console.log('Error establishing the connection: ', error))

      // should be refactored
      this.hubConnection.on('ReceiveFirstWin', (auction: Auction) => {
        runInAction(() => {
          auction.deadline = new Date(this.filterDateLocal(auction.deadline))
          this.auctionDetails!.status = auction.status
          this.auctionDetails!.winner = auction.winner
          this.auctionDetails!.deadline = auction.deadline
          this.auctionDetails!.status = auction.status
        })
      })

      this.hubConnection.on('DocumentReceived', (document: Solvency) => {
        runInAction(() => {
          this.auctionDetails?.solvencyAttachment?.push(document)
        })
      })

      this.hubConnection.on('DocumentDeleted', (solvencyId: string) => {
        runInAction(() => {
          this.auctionDetails!.solvencyAttachment = this.auctionDetails?.solvencyAttachment?.filter(
            (solvency) => solvency.id !== solvencyId,
          )
        })
      })

      // should be refactored
      this.hubConnection.on('ValidateSolvency', (solvencyId: string, validId: number) => {
        runInAction(() => {
          this.auctionDetails!.solvencyAttachment = this.auctionDetails?.solvencyAttachment?.map(
            (solvencyItem) => {
              if (solvencyItem.id === solvencyId) {
                solvencyItem.valid = validId
              }
              return solvencyItem
            },
          )
        })
      })

      this.hubConnection.on('ReceiveBid', (bid: BidsLive) => {
        runInAction(() => {
          bid.deadline = this.filterDateLocal(bid.deadline)
          bid.createdAt = dayjs(bid.createdAt).toString() // .format('MMMM D, YYYY h:mm A')
          this.currentBid = bid.bid
          this.latestBidder = bid
          this.auctionDetails!.deadline = bid.deadline
          this.bids.unshift(bid)
          this.updateCurrentBidWithCurrency()
        })
      })

      this.hubConnection.on('UserCountUpdated', (count: number) => {
        runInAction(() => {
          this.userCount = count
        })
      })

      // should be refactored
      this.hubConnection.on('QueuePosition', (position: number) => {
        runInAction(() => {
          if (position === 0) {
            this.queueTimer = 3

            const timerInterval = setInterval(() => {
              runInAction(() => {
                this.queueTimer -= 1

                if (this.queueTimer === 0) {
                  this.queuePosition = position
                  clearInterval(timerInterval)
                }
              })
            }, 1000)
          } else {
            this.queuePosition = position
          }
        })
      })

      this.hubConnection.on('AuctionEdit', (auctionId: string) => {
        // update auction details
        this.loadAuctionDetails(auctionId)
      })
    }
  }

  stopHubConnection = () => {
    this.hubConnection
      ?.stop()
      .then(() => {
        this.queuePosition = 0
      })
      .catch((error) => {
        console.log('Error stopping connection: ', error)
      })
  }

  clearActionDetails = () => {
    this.auctionDetails = undefined
  }

  clearBids = () => {
    this.bids = []
    this.stopHubConnection()
  }

  addBid = async (values: any) => {
    if (store.userStore.isLoggedIn) {
      values.bid *= 100
      this.setLoadingButton(true)
      // Create a new promise for the current bid request
      const bidPromise = new Promise<void>((resolve, reject) => {
        try {
          const sendBidAsync = async () => {
            // Assume this.hubConnection is an instance of some class with an 'invoke' method
            await this.hubConnection?.invoke('SendBid', values)
          }

          sendBidAsync()
            .then(() => {
              resolve()
            })
            .catch((error) => {
              reject(error)
            })
        } catch (error) {
          reject(error)
        }
      })

      // If there's no bid request currently being processed, process the current one
      if (!this.bidQueue) {
        this.bidQueue = bidPromise
        try {
          await this.bidQueue
        } catch (error) {
          console.log(error)
        } finally {
          this.bidQueue = null
        }
      } else {
        // If there's a bid request currently being processed, add the current one to the queue
        this.bidQueue = this.bidQueue.then(() => bidPromise)
      }
      this.setLoadingButton(false)
    }
  }

  handleBuyNow = async (values: any) => {
    await this.hubConnection?.invoke('AddFirstWinner', values)
  }

  privateAuctionValidation = (bidding: Auction, biddingId: string) => {
    if (!store.userStore.isLoggedIn) {
      const { location } = window
      const params = new URLSearchParams(location.search)

      const validateEmail = params.get('validateEmail') as string
      const token = params.get('token')?.replace(/ /g, '+') as string

      const queryParams = {
        auctionId: biddingId,
        token,
        validateEmail,
      }

      history.push({
        pathname: '/login',
        search: new URLSearchParams(queryParams).toString(),
      })
    } else {
      let authorized = true
      let authorizedMod = true
      // Check if current user is authorized
      if (bidding.authorizedUsers?.length === 0) {
        authorized = false
      } else {
        const isAuthorized = bidding.authorizedUsers?.some(
          (bidder) => bidder.user?.email === store.userStore.user?.email,
        )
        if (!isAuthorized) {
          authorized = false
        }
      }
      // Check if current Moderator is authorized
      if (bidding.authorizedMods?.length === 0) {
        authorizedMod = false
      } else {
        const isAuthorized = bidding.authorizedMods?.some(
          (mod) => (mod as AuthorizedModInterface).user.email === store.userStore.user?.email,
        )
        if (!isAuthorized) {
          authorizedMod = false
        }
      }

      // If none are authorized open unauthorized modal
      if (!authorized && !authorizedMod) {
        this.setUnauthorizedUser(true)
      }
    }
  }

  auctionFilter = (auction: Auction) => {
    runInAction(async () => {
      auction.startingDate = new Date(this.filterDateLocal(auction.startingDate))
      auction.deadline = new Date(this.filterDateLocal(auction.deadline))
      this.auctionDetails = auction

      // If there is bids inside auction
      if (auction.bids?.length) {
        auction.bids.forEach((bid) => {
          bid.createdAt = this.filterDateLocal(bid.createdAt)
          bid.createdAt = dayjs(bid.createdAt).format('MMMM D, YYYY h:mm A')
        })
        const highestBid = auction.bids.reduce((prevBid, currentBid) =>
          prevBid.bid > currentBid.bid ? prevBid : currentBid,
        )
        this.latestBidder = auction.bids[0]
        this.currentBid = highestBid.bid
        this.bids = auction.bids
        this.updateCurrentBidWithCurrency()
      } else {
        this.currentBid = 0
        this.latestBidder = null
      }
      this.deadline = auction.deadline

      await new Promise((resolve) => setTimeout(resolve, 400))
      this.setLoadingInitial(false)
    })
  }

  filterDateLocal = (date: Date) => store.methodsStore.formatDateLocal(date)

  updateCurrentBidWithCurrency = async () => {
    const convertBid = this.currentBid
    const selectedCurrencyCode =
      store.methodsStore.currencyCodes[this.auctionDetails?.post?.currency || 0]

    const { selectedCurrency } = store.methodsStore

    try {
      const currency = await store.exchangeCurrencyStore.fetchCurrency(
        selectedCurrencyCode,
        selectedCurrency,
        convertBid,
      )

      if (typeof currency === 'number') {
        runInAction(() => {
          this.convertedBid = currency
        })
      }
    } catch (error) {
      console.error('Error updating convertedBid:', error)
    }
  }
}
